import type { StateCreator } from 'zustand';
import type { User } from '../types';
import { api } from '../services/api';
import { usersApi } from '../services/usersApi';

export interface AuthSlice {
  token: string | null;
  user: User | null;
  loginError: string | null;
  // When the admin is "viewing as" another user, this holds the impersonated user.
  // The admin's own session is parked in localStorage (admin_token/admin_user) and
  // restored by stopImpersonating().
  impersonating: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => void;
  impersonate: (userId: number) => Promise<boolean>;
  stopImpersonating: () => void;
}

function loadStoredUser(): { token: string | null; user: User | null; impersonating: User | null } {
  try {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const impersonating = JSON.parse(localStorage.getItem('impersonating') || 'null');
    return { token, user, impersonating };
  } catch {
    console.warn('Corrupt user data in localStorage, clearing');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    return { token: null, user: null, impersonating: null };
  }
}

export const createAuthSlice: StateCreator<AuthSlice> = (set) => ({
  ...loadStoredUser(),
  loginError: null,

  login: async (username, password) => {
    try {
      const res = await api.post<{ token: string; user: User }>('/login', { username, password });
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      set({ token: res.token, user: res.user, loginError: null });
      // Redirect to change-password if forced
      if (res.user.must_change_password) {
        window.location.href = '/change-password';
        return true;
      }
      return true;
    } catch (e) {
      const msg = e instanceof TypeError
        ? 'Server nicht erreichbar'
        : (e as Error).message;
      set({ loginError: msg });
      return false;
    }
  },

  logout: () => {
    // Clear the live session AND any parked admin-impersonation session.
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    set({ token: null, user: null, impersonating: null });
    window.location.href = '/login';
  },

  checkAuth: () => {
    const { token, user, impersonating } = loadStoredUser();
    set({ token, user, impersonating });
  },

  // Admin-only: mint a token for `userId` and switch the whole app to that user's view.
  // The current admin session is parked under admin_token/admin_user. A hard reload
  // ensures every cached slice (leads, view config, branding) is refetched as the new user.
  impersonate: async (userId) => {
    try {
      const res = await usersApi.impersonate(userId);
      const curToken = localStorage.getItem('token');
      const curUser = localStorage.getItem('user');
      // Only park the admin session the first time (don't overwrite if already impersonating).
      if (curToken && curUser && !localStorage.getItem('admin_token')) {
        localStorage.setItem('admin_token', curToken);
        localStorage.setItem('admin_user', curUser);
      }
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      localStorage.setItem('impersonating', JSON.stringify(res.user));
      // The selected send-account belongs to the admin's context and would send a
      // foreign scope_owner as the impersonated (setter) role → 403 "can only scope
      // to yourself". Clear it so the impersonated user starts with no account picked.
      localStorage.removeItem('fraud0SelectedLinkedInAccount');
      set({ token: res.token, user: res.user, impersonating: res.user });
      window.location.href = '/app';
      return true;
    } catch (e) {
      set({ loginError: (e as Error).message });
      return false;
    }
  },

  // Restore the parked admin session. Pure client-side (no API call), so it works even
  // while the impersonated scoped role would 403 every other endpoint.
  stopImpersonating: () => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    // Switching identity back to admin: drop any send-account the impersonated user
    // picked so it doesn't leak into the admin's scope (mirror of impersonate()).
    localStorage.removeItem('fraud0SelectedLinkedInAccount');
    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      set({ token: adminToken, user: JSON.parse(adminUser), impersonating: null });
      window.location.href = '/app';
    } else {
      // No parked admin session — fall back to a clean logout.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      set({ token: null, user: null, impersonating: null });
      window.location.href = '/login';
    }
  },
});
